<template>
  <div v-if="currentModal">
    <div class="modal-background" />
    <transition name="slide-inside">
      <div :class="['modal-wrapper', currentModal.customClass]" v-if="showContent">
        <div class="modal-title">
          <span>{{ currentModal.title }}</span>
          <a v-if="currentModal.link" :href="currentModal.link" target="_blank">
            <div class="question-mark" />
          </a>
        </div>
        <template v-if="currentModal.useCustomContent">
          <slot
            name="custom-content"
            :modal="currentModal"
          ></slot>
        </template>
        <template v-else>
          <div
            v-for="text in currentModal.content"
            :key="text"
            class="modal-text-content"
          >
            {{ text }}
          </div>
        </template>
        <div v-if="currentModal.isInput" class="modal-input-container">
          <input
            ref="inp"
            @focus="clearStyle"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            type="text"
            :placeholder="currentModal.inputPlaceholder"
            :class="['modal-input', currentModal.inputClass, { 'invalid-input': isInvalid }]"
          />
        </div>
        <slot
          v-if="currentModal.useCustomButtons"
          name="custom-buttons"
          :modal="currentModal"
        ></slot>
        <div v-else class="modal-btn-container">
          <div
            class="modal-btn btn-confirm"
            v-if="currentModal.btnConfirm"
            @click="confirm"
          >
            {{ currentModal.btnConfirm }}
          </div>
          <div
            class="modal-btn btn-cancel"
            v-if="currentModal.btnCancel"
            @click="hide(true)"
          >
            {{ currentModal.btnCancel }}
          </div>
        </div>
        <div class="env-close btn-close" @click="hide" />
        <div v-if="currentModal.noShow" class="modal-noshow-button">
          <label>
            <check-box
              class="noshow-checkbox"
              :checked="isNoshowAgain"
              @change="updateNoshow"
            />
            <span class="noshow-label">{{ $t('DIALOG.str61') }}</span>
          </label>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import CheckBox from '@/components/common/CheckBox';
import { Modal } from '@/js/plugin';
import { EventBus, getBit, setBit, isStr, isFunction } from '@/js/utils';

export default {
  name: 'CommonModal',
  components: {
    CheckBox,
  },
  props: {
    modal: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      modalList: [],
      showContent: false,
      isInvalid: false,
      isNoshowAgain: false,
      inputValue: '',
    };
  },
  computed: {
    currentModal() {
      // Use the prop if provided, otherwise fall back to the last item in modalList
      return (
        this.modal ||
        (this.modalList.length > 0
          ? this.modalList[this.modalList.length - 1]
          : null)
      );
    },
    userPreferences() {
      return this.$store.getters['user/userPreferences'];
    },
  },
  methods: {
    updateNoshow(notShow) {
      this.isNoshowAgain = notShow;
    },
    hide(isCanceled) {
      if (isCanceled && isFunction(this.currentModal.onCancel)) {
        this.currentModal.onCancel();
      }
      if (this.isNoshowAgain && this.currentModal.noShow) {
        EventBus.$emit('set-userinfo', {
          user_preferences: setBit(
            this.userPreferences,
            this.currentModal.noShow
          ),
        });
      }
      if (this.modalList.length > 0) {
        this.modalList.pop();
      }
      this.isNoshowAgain = false;
      this.showContent = false;
      this.isInvalid = false;
      this.$emit('hide', isCanceled);
    },
    confirm() {
      if (isFunction(this.currentModal.onConfirm)) {
        let data;
        if (this.currentModal.isInput) {
          data = this.$refs.inp.value;
          if (isFunction(this.currentModal.inputValidation)) {
            if (!this.currentModal.inputValidation(data)) {
              this.isInvalid = true;
              return;
            }
          }
        }
        this.currentModal.onConfirm(data);
        if (this.isNoshowAgain && this.currentModal.noShow) {
          EventBus.$emit('set-userinfo', {
            user_preferences: setBit(
              this.userPreferences,
              this.currentModal.noShow
            ),
          });
        }
        this.hide();
      } else {
        this.hide();
      }
      this.$emit('confirm');
    },
    show(params) {
      if (this.modal) return; // Skip event bus if prop is used
      this.showContent = false;
      this.isInvalid = false;
      const modalParams = {
        title: params.title,
        content: isStr(params.content) ? [params.content] : params.content,
        isInput: params.isInput,
        inputPlaceholder: params.inputPlaceholder || '',
        link: params.link,
        btnCancel: params.btnCancel,
        btnConfirm: params.btnConfirm,
        onConfirm: params.onConfirm,
        onCancel: params.onCancel,
        inputValidation: params.inputValidation,
        noShow: params.noShow,
        defaultValue: params.defaultValue || '',
        useCustomContent: params.useCustomContent || false,
        useCustomButtons: params.useCustomButtons || false,
        customClass: params.customClass || '',
        inputClass: params.inputClass || '',
      };
      if (
        modalParams.noShow &&
        getBit(this.userPreferences, modalParams.noShow)
      ) {
        if (isFunction(params.onConfirm)) params.onConfirm();
      } else {
        this.modalList.push(modalParams);
        this.$nextTick().then(() => {
          this.showContent = true;
          this.$nextTick().then(() => {
            if (modalParams.isInput) {
              this.$refs.inp.focus();
              this.$refs.inp.value = modalParams.defaultValue;
            }
          });
        });
      }
    },
    clearStyle() {
      this.isInvalid = false;
    },
  },
  watch: {
    modal: {
      immediate: true,
      handler(newModal) {
        if (newModal) {
          this.showContent = false; // Ensure initial state is false
          this.$nextTick().then(() => {
            this.showContent = true; // Trigger animation
            this.$nextTick().then(() => {
              if (newModal.isInput && this.$refs.inp) {
                this.$refs.inp.focus();
                this.$refs.inp.value = newModal.defaultValue || '';
              }
            });
          });
        } else {
          this.showContent = false; // Reset when modal prop is cleared
        }
      },
    },
  },
  beforeMount() {
    if (!this.modal) {
      Modal.EventBus.$on('show', this.show);
    }
    window.onkeydown = (event) => {
      if (event.keyCode === 13 && this.currentModal) {
        if (typeof this.currentModal.isCustomInputValid === 'function') {
          if (!this.currentModal.isCustomInputValid()) {
            return;
          }
        }
        this.confirm();
      }
    };
  },
  beforeDestroy() {
    if (!this.modal) {
      Modal.EventBus.$off();
    }
    window.onkeydown = null;
  },
};
</script>
<style scoped>
/* Default modal wrapper */
.modal-wrapper {
  z-index: 10001;
  position: fixed;
  top: 50%;
  left: 50%;
  width: 80%;
  transform: translate(-50%, -50%);
  padding: 25px 20px 20px;
  max-width: 420px;
  min-width: 250px;
  border-radius: 5px;
  overflow: hidden;
  font-size: 1.1em;
  line-height: 1.6;
  word-break: break-word;
  background-color: #ffffff;
  cursor: default;
  box-shadow: 0 7px 8px -2px rgba(0, 0, 0, 0.2);
  text-align: center;
}

/* Custom class for notification email modal */
.modal-noti-email {
  padding: 25px 20px 20px;
}
.modal-noti-email .modal-text-content,
.modal-noti-email .modal-input-container,
.modal-noti-email .modal-btn-container {
  width: calc(100% - 2.5em - 20px);
  margin-left: auto;
  margin-right: auto;
}
.modal-noti-email .modal-input {
  width: 100%;
  color: #a0a0a0;
  border-bottom-color: #a0a0a0;
  text-align: center;
}
.modal-noti-email .modal-btn-container {
  padding: 10px 0;
  display: flex;
  gap: 1.5em;
  justify-content: space-between;
}
.modal-noti-email .modal-btn {
  flex: 1;
  margin: 0;
  min-width: 0;
  padding: 5px 1em;
}

.modal-noti-email .modal-title {
  margin-bottom: 2.5px;
}

.modal-noti-email .modal-text-content + .modal-text-content {
  margin-top: 2.5px;
}

.modal-title {
  font-size: 1.25em;
  font-weight: bold;
  margin: 5px 0 15px 0;
}

.modal-title > * {
  vertical-align: bottom;
}

.modal-btn-container {
  padding-top: 10px;
  text-align: center;
}

.btn-confirm {
  color: #fff;
  background-color: #ed7e57;
  border: 1px solid #ed7e57;
}

.btn-confirm:hover {
  background-color: #e74a1f;
}

.btn-cancel {
  color: #898989;
  border: 1px solid #898989;
}

.btn-cancel:hover {
  background-color: #898989;
  color: #f2f2f2;
}

.modal-btn {
  display: inline-block;
  margin: 10px 2%;
  padding: 5px 3%;
  text-align: center;
  border-radius: 20px;
  min-width: 30%;
  cursor: pointer;
  transition: 0.3s ease-in-out;
}

.btn-close {
  position: absolute;
  width: 18px;
  height: 18px;
  top: 10px;
  right: 10px;
  cursor: pointer;
}

.modal-background {
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 10000;
  overflow: auto;
}

.modal-text-content + .modal-text-content {
  margin-top: 5px;
}

.modal-input-container {
  text-align: center;
  margin: 10px 0;
  position: relative;
}

.modal-input {
  -webkit-appearance: none;
  appearance: none;
  border: none;
  outline: none;
  border-radius: 0;
  border-bottom: 1px solid #2f2f2f;
  font-size: 1.1em;
  width: 80%;
}

.modal-noshow-button {
  font-size: 0.9em;
  color: #898989;
  position: relative;
  margin-top: 5px;
  height: 1em;
}

.noshow-checkbox {
  font-size: 0.8em;
  margin: 0 0.2em 0.2em;
}

.noshow-label {
  cursor: pointer;
}

.invalid-input {
  box-shadow: 0 0 2px 4px rgba(255, 0, 0, 0.4);
}

.slide-inside-enter,
.slide-inside-leave-to {
  top: 25%;
  opacity: 0;
}

.slide-inside-enter-active,
.slide-inside-leave-active {
  transition: 0.2s ease-out;
}
</style>
